import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';
import ReporteFiltroCuotas from './ReporteFiltroCuotas';
import ReporteFiltroInteres from './ReporteFiltroInteres';

const FiltrarPagos = () => {
  const [isGroupSearch, setIsGroupSearch] = useState(false);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [estado, setEstado] = useState('');
  const [selectedAsesor, setSelectedAsesor] = useState('');
  const [abonadoPor, setAbonadoPor] = useState('');
  const [groups, setGroups] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [cuotas, setCuotas] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAsesores, setLoadingAsesores] = useState(true);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [isVencidoFilterActive, setIsVencidoFilterActive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelUrl, setExcelUrl] = useState(null);
  const [showExcelReportViewer, setShowExcelReportViewer] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupsResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/cronograma/grupos`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!groupsResponse.ok) throw new Error('Error al cargar grupos');
        const groupsData = await groupsResponse.json();
        setGroups(Array.isArray(groupsData) ? groupsData : []);

        setLoadingAsesores(true);
        const asesoresResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/asesores`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!asesoresResponse.ok) throw new Error('Error al cargar asesores');
        const asesoresData = await asesoresResponse.json();
        const validAsesores = Array.isArray(asesoresData.asesores)
          ? asesoresData.asesores.filter(asesor => asesor && asesor.id && asesor.nombre)
          : [];
        setAsesores(validAsesores);
        if (validAsesores.length === 0) {
          setError('No se encontraron asesores válidos');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Error al cargar datos');
      } finally {
        setLoadingAsesores(false);
      }
    };
    fetchData();
  }, []);

  // Función para manejar cambio de estado
  const handleEstadoChange = (newEstado) => {
    setEstado(newEstado);
    
    // Si selecciona "vence_hoy", establecer fecha del día automáticamente
    if (newEstado === 'vence_hoy') {
      const today = format(new Date(), 'yyyy-MM-dd');
      setStartDate(today);
      setEndDate(today);
      setIsVencidoFilterActive(false);
    } 
    // Si selecciona "vencido", activar modo de filtro sin fechas obligatorias
    else if (newEstado === 'vencido') {
      setIsVencidoFilterActive(true);
    } 
    // Para otros estados, desactivar modo vencido
    else {
      setIsVencidoFilterActive(false);
    }
  };

  const handleFilter = async () => {
    // Validación: solo requerir fechas si NO es filtro de vencidos
    if (!isVencidoFilterActive && (!startDate || !endDate)) {
      setError('Por favor, selecciona ambas fechas.');
      return;
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      setError('La fecha de fin debe ser posterior o igual a la fecha de inicio.');
      return;
    }
    if (isGroupSearch && !selectedGroup) {
      setError('Por favor, selecciona un grupo.');
      return;
    }

    setError('');
    setCuotas([]);
    setLoading(true);

    try {
      const payload = {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(estado ? { estado } : {}),
        ...(isGroupSearch && selectedGroup ? { nombreGrupo: selectedGroup } : {}),
        ...(busquedaCliente ? { busquedaCliente } : {}),
        ...(selectedAsesor ? { idAsesor: parseInt(selectedAsesor, 10) } : {}),
        ...(abonadoPor ? { abonadoPor } : {}),
      };

      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/pagos/filtrar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al filtrar pagos');
      }

      const data = await response.json();
      setCuotas(data.cuotas || []);
      if (!data.cuotas.length) {
        setError(
          `No se encontraron pagos${estado ? ` con estado "${estado}"` : ''} en el rango de fechas.`
        );
      }
    } catch (err) {
      setError(err.message || 'Error al filtrar pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setIsGroupSearch(false);
    setBusquedaCliente('');
    setSelectedGroup('');
    setStartDate('');
    setEndDate('');
    setEstado('');
    setSelectedAsesor('');
    setAbonadoPor('');
    setCuotas([]);
    setError('');
    setShowReportViewer(false);
    setShowModal(false);
    setPdfUrl(null);
    setShowExcelModal(false);
    setExcelUrl(null);
    setIsVencidoFilterActive(false);
  };

  const handleGeneratePDF = () => {
    setShowReportViewer(true);
  };

  const handlePDFGenerated = (url) => {
    setPdfUrl(url);
    setShowModal(true);
  };

  const handlePDFComplete = () => {
    setShowReportViewer(false);
  };

  const closeModal = () => {
    setShowModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleGenerateExcel = () => {
    setShowExcelReportViewer(true);
  };

  const handleExcelGenerated = (url) => {
    if (url) {
      setExcelUrl(url);
      setShowExcelModal(true);
    }
  };

  const handleExcelComplete = () => {
    setShowExcelReportViewer(false);
  };

  const closeExcelModal = () => {
    setShowExcelModal(false);
    if (excelUrl) {
      URL.revokeObjectURL(excelUrl);
      setExcelUrl(null);
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return '-';
    }
  };

  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getOtros = (cuota) => (
    cuota.otros !== null
      ? toNumber(cuota.otros)
      : toNumber(cuota.monto) - toNumber(cuota.capital) - toNumber(cuota.interes)
  );

  const getStatusStyles = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800';
      case 'pagado':
        return 'bg-green-100 text-green-800';
      case 'vence_hoy':
        return 'bg-orange-100 text-orange-800';
      case 'vencido':
        return 'bg-red-100 text-red-800';
      case 'prepagado':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200';
  const selectClass = inputClass;
  const primaryButtonClass = 'w-full md:w-auto rounded-xl bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400';
  const secondaryButtonClass = 'w-full md:w-auto rounded-xl border border-red-500 bg-white px-6 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50';

  return (
    <div className="space-y-6">
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Control de cartera</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Filtrar pagos</h2>
          <p className="mt-2 text-sm text-slate-600">
            Consulta pagos por cliente, estado, fecha, asesor, grupo y fuente de abono.
          </p>
        </div>
      </section>

      {showReportViewer && (
        <ReporteFiltroCuotas
          key={`report-pagos-${Date.now()}`}
          cuotas={cuotas}
          selectedAsesor={selectedAsesor}
          asesores={asesores}
          startDate={startDate}
          endDate={endDate}
          estado={estado}
          dni={busquedaCliente}
          selectedGroup={selectedGroup}
          abonadoPor={abonadoPor}
          onComplete={handlePDFComplete}
          onPDFGenerated={handlePDFGenerated}
        />
      )}

      {showExcelReportViewer && (
        <ReporteFiltroInteres
          cuotas={cuotas}
          selectedAsesor={selectedAsesor}
          asesores={asesores}
          startDate={startDate}
          endDate={endDate}
          estado={estado}
          dni={busquedaCliente}
          selectedGroup={selectedGroup}
          abonadoPor={abonadoPor}
          onComplete={handleExcelComplete}
          onExcelGenerated={handleExcelGenerated}
        />
      )}

      {/* Modal PDF */}
      {showModal && pdfUrl && (
        <div className="modal-overlay">
          <div className="modal-panel modal-panel-lg flex h-[80vh] flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h2 className="text-lg font-semibold text-slate-900">Vista previa del reporte de pagos</h2>
              <button
                onClick={closeModal}
                className="text-slate-500 transition hover:text-slate-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Excel */}
      {showExcelModal && (
        <div className="modal-overlay">
          <div className="modal-panel modal-panel-md flex max-h-[80vh] flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h2 className="text-lg font-semibold text-slate-900">Reporte Excel generado</h2>
              <button
                onClick={closeExcelModal}
                className="text-slate-500 transition hover:text-slate-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="text-center">
                <div className="mb-6">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    ¡Reporte Excel Generado Exitosamente!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    El archivo Excel con el reporte de intereses ha sido generado correctamente.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-6">
                  <h4 className="mb-3 font-medium text-slate-900">Información del reporte:</h4>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>Total de registros:</span>
                      <span className="font-medium">{cuotas.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Periodo:</span>
                      <span className="font-medium">
                        {startDate ? formatDate(startDate) : 'No especificado'} - {endDate ? formatDate(endDate) : 'No especificado'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Estado:</span>
                      <span className="font-medium">{estado || 'Todos'}</span>
                    </div>
                    {selectedAsesor && (
                      <div className="flex justify-between">
                        <span>Asesor:</span>
                        <span className="font-medium">
                          {asesores.find(asesor => asesor.id === parseInt(selectedAsesor))?.nombre || 'No encontrado'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {excelUrl && (
                    <a
                      href={excelUrl}
                      download="Reporte_Intereses_Cuotas.xlsx"
                    className="inline-flex items-center justify-center rounded-xl bg-green-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-green-700"
                  >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Descargar Excel
                    </a>
                  )}
                  <button
                    onClick={closeExcelModal}
                    className="rounded-xl bg-slate-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="ui-card p-6">
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="groupSearch"
            checked={isGroupSearch}
            onChange={() => setIsGroupSearch(!isGroupSearch)}
            className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
          />
          <label htmlFor="groupSearch" className="ml-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
            Buscar por grupo
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {isGroupSearch ? (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SELECCIONAR GRUPO
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className={selectClass}
              >
                <option value="">SELECCIONE GRUPO</option>
                {groups.map(group => (
                  <option key={group.idGrupo} value={group.nombre}>
                    {group.nombre}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                BÚSQUEDA DE CLIENTE (DNI, NOMBRE, APELLIDOS, ID)
              </label>
              <input
                type="text"
                value={busquedaCliente}
                onChange={(e) => setBusquedaCliente(e.target.value.toUpperCase())}
                placeholder="DNI, NOMBRE, APELLIDOS O ID CLIENTE"
                className={inputClass}
              />
            </div>
          )}
          <div>
            <label className={labelClass}>
              FECHA INICIO {!isVencidoFilterActive && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputClass}
            />
            {isVencidoFilterActive && (
              <p className="text-xs text-gray-500 mt-1">Opcional para filtro "Vencido"</p>
            )}
          </div>
          <div>
            <label className={labelClass}>
              FECHA FIN {!isVencidoFilterActive && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputClass}
            />
            {isVencidoFilterActive && (
              <p className="text-xs text-gray-500 mt-1">Opcional para filtro "Vencido"</p>
            )}
          </div>
          <div>
            <label className={labelClass}>
              ESTADO
            </label>
            <select
              value={estado}
              onChange={(e) => handleEstadoChange(e.target.value)}
              className={selectClass}
            >
              <option value="">TODOS</option>
              <option value="pendiente">PENDIENTE</option>
              <option value="pagado">PAGADO</option>
              <option value="vence_hoy">VENCE HOY</option>
              <option value="vencido">VENCIDO</option>
              <option value="prepagado">PREPAGADO</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>
              ASESOR
            </label>
            <select
              value={selectedAsesor}
              onChange={(e) => setSelectedAsesor(e.target.value)}
              disabled={loadingAsesores}
              className={`${selectClass} ${loadingAsesores ? 'cursor-not-allowed bg-gray-100' : ''}`}
            >
              {loadingAsesores ? (
                <option value="">CARGANDO...</option>
              ) : (
                <>
                  <option value="">TODOS</option>
                  {asesores.map(asesor => (
                    <option key={asesor.id} value={asesor.id}>
                      {asesor.nombre}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div>
            <label className={labelClass}>
              ABONADO POR
            </label>
            <select
              value={abonadoPor}
              onChange={(e) => setAbonadoPor(e.target.value)}
              className={selectClass}
            >
              <option value="">TODOS</option>
              <option value="CUENTA CORRIENTE">CUENTA CORRIENTE</option>
              <option value="CAJA CHICA">CAJA CHICA</option>
            </select>
          </div>
          <div className="md:self-end md:col-span-6 flex flex-col md:flex-row gap-4">
            <button
              onClick={handleFilter}
              disabled={((!isVencidoFilterActive && (!startDate || !endDate)) || (isGroupSearch && !selectedGroup)) || loading}
              className={primaryButtonClass}
            >
              {loading ? 'Filtrando...' : 'Filtrar'}
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              className={secondaryButtonClass}
            >
              LIMPIAR FILTROS
            </button>
          </div>
        </div>
      </section>

      {loading && (
        <div className="flex justify-center mb-6">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-red-600 border-t-transparent"></div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      {cuotas.length > 0 && (
        <section className="ui-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-slate-900">
              PAGOS
            </h3>
            <div className="flex gap-3">
              {/* Botón PDF (Rojo) */}
              <button
                onClick={handleGeneratePDF}
                disabled={cuotas.length === 0}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                aria-label="Ver reporte de pagos en PDF"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 inline-block mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                PDF
              </button>

              {/* Botón Excel (Verde) */}
              <button
                onClick={handleGenerateExcel}
                disabled={cuotas.length === 0}
                className="rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                aria-label="Generar reporte de intereses en Excel"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 inline-block mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                EXCEL
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[1550px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="py-4 px-2">ID PRESTAMO</th>
                  <th className="py-4 px-6">CLIENTE</th>
                  <th className="py-4 px-6">DNI</th>
                  <th className="py-4 px-2 text-center">N° CUOTA</th>
                  <th className="py-4 px-4 text-center">VENCIMIENTO</th>
                  <th className="py-4 px-3 text-center">F. PAGO</th>
                  <th className="py-4 px-3 text-center">CAPITAL</th>
                  <th className="py-4 px-3 text-center">INTERÉS</th>
                  <th className="py-4 px-3 text-center">OTROS</th>
                  <th className="py-4 px-3 text-center">CUOTA</th>
                  <th className="py-4 px-3 text-center">SALDO PENDIENTE</th>
                  <th className="py-4 px-2 text-center">DIAS MORA</th>
                  <th className="py-4 px-6 text-center">MORA</th>
                  <th className="py-4 px-6">ESTADO</th>
                  <th className="py-4 px-6">ASESOR</th>
                  <th className="py-4 px-6">ABONADO POR</th>
                  <th className="py-4 px-6">OBSERVACIONES</th>
                </tr>
              </thead>
              <tbody>
                {cuotas.map(cuota => {
                  const otros = getOtros(cuota);
                  const saldoPendiente = toNumber(cuota.saldo_pendiente);

                  return (
                    <tr
                      key={cuota.idCuota}
                      className="border-b border-slate-100 transition hover:bg-slate-50 last:border-b-0"
                    >
                      <td className="py-4 px-2">{cuota.idPrestamo}</td>
                      <td className="py-4 px-6">{cuota.cliente_nombre}</td>
                      <td className="py-4 px-6">{cuota.cliente_dni}</td>
                      <td className="py-4 px-1 text-center">{cuota.numero_cuota}</td>
                      <td className="py-4 px-4 text-center">{formatDate(cuota.fecha_vencimiento)}</td>
                      <td className="py-4 px-6 text-center">{formatDate(cuota.fechaPago)}</td>
                      <td className="py-4 px-3 text-center">{toNumber(cuota.capital).toFixed(2)}</td>
                      <td className="py-4 px-3 text-center">{toNumber(cuota.interes).toFixed(2)}</td>
                      <td className="py-4 px-3 text-center">{otros.toFixed(2)}</td>
                      <td className="py-4 px-3 text-center">{toNumber(cuota.monto).toFixed(2)}</td>
                      <td className="py-4 px-3 text-center font-semibold">
                        {saldoPendiente.toFixed(2)}
                      </td>
                      <td className="py-4 px-2 text-center">{cuota.dias_mora}</td>
                      <td className="py-4 px-6 text-center">
                        S/ {toNumber(cuota.mora).toFixed(2)}
                        {cuota.mora_reducida > 0 && (
                          <span className="text-xs text-green-600 ml-1">(-{cuota.mora_reducida}%)</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusStyles(
                            cuota.estado
                          )}`}
                        >
                          {cuota.estado}
                        </span>
                      </td>
                      <td className="py-4 px-6">{cuota.asesor_nombre}</td>
                      <td className="py-4 px-6">{cuota.abonado_por}</td>
                      <td className="py-4 px-6">{cuota.observaciones || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-700">
                  <td colSpan="6" className="py-4 px-6">
                    TOTALES
                  </td>
                  <td className="py-4 px-3 text-center">
                    {cuotas.reduce((sum, cuota) => sum + toNumber(cuota.capital), 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-3 text-center">
                    {cuotas.reduce((sum, cuota) => sum + toNumber(cuota.interes), 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-3 text-center">
                    {cuotas.reduce(
                      (sum, cuota) =>
                        sum + getOtros(cuota),
                      0
                    ).toFixed(2)}
                  </td>
                  <td className="py-4 px-3 text-center">
                    {cuotas.reduce((sum, cuota) => sum + toNumber(cuota.monto), 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-3 text-center">
                    {cuotas.reduce((sum, cuota) => sum + toNumber(cuota.saldo_pendiente), 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-2 text-center"></td>
                  <td className="py-4 px-6 text-center"></td>
                  <td className="py-4 px-6"></td>
                  <td className="py-4 px-6"></td>
                  <td className="py-4 px-6"></td>
                  <td className="py-4 px-6"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default FiltrarPagos;
//Mejora de Filtrar Pagos
